import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Stethoscope, X, Pencil, Check, AlertCircle, Mail, Phone, Briefcase, Building2, MapPin, Search } from "lucide-react";
import { US_STATES } from "@/lib/usStates";

export default function ProvidersPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "admin" || user?.role === "organizer";
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState({ email: "", full_name: "", role: "doctor", specialty: "", phone: "", password: "", department: "", office_address: "", office_directions: "", city: "", state: "", zip: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const [editMsg, setEditMsg] = useState({ kind: "", text: "" });
  const [editRole, setEditRole] = useState("doctor");
  const [originalRole, setOriginalRole] = useState("doctor");
  const [query, setQuery] = useState("");

  const filteredList = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      [p.full_name, p.email, p.role, p.specialty, p.department, p.phone, p.office_address, p.city, p.state]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  })();

  const load = () => api.get("/providers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/providers", form);
      setCreated(r.data);
      setForm({ email: "", full_name: "", role: "doctor", specialty: "", phone: "", password: "", department: "", office_address: "", office_directions: "", city: "", state: "", zip: "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditForm({ full_name: p.full_name || "", email: p.email || "", phone: p.phone || "", specialty: p.specialty || "", status: p.status || "active", department: p.department || "", office_address: p.office_address || "", office_directions: p.office_directions || "", city: p.city || "", state: p.state || "", zip: p.zip || "" });
    setEditRole(p.role || "doctor");
    setOriginalRole(p.role || "doctor");
    setEditMsg({ kind: "", text: "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditBusy(true); setEditMsg({ kind: "", text: "" });
    try {
      await api.patch(`/providers/${editing.id}`, editForm);
      // If the role was changed in the edit modal, persist it via the dedicated role endpoint.
      if (editRole && editRole !== originalRole) {
        await api.patch(`/providers/${editing.id}/role`, { role: editRole });
      }
      setEditMsg({ kind: "success", text: "Saved." });
      load();
      setTimeout(() => { setEditing(null); setEditMsg({ kind: "", text: "" }); }, 700);
    } catch (err) {
      setEditMsg({ kind: "error", text: err.response?.data?.detail || "Save failed." });
    } finally { setEditBusy(false); }
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="providers-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Providers</h1>
          <p className="text-stone-500 mt-1">{isSuper ? "Manage doctors, nurses and admins." : "View your care-team directory."}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
            <input
              data-testid="providers-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, specialty…"
              className="w-72 pl-9 pr-9 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm bg-white"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid="providers-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-stone-400 hover:text-stone-700"
                title="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {isSuper && (
            <button onClick={() => { setShow(true); setCreated(null); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold" data-testid="add-provider-btn">
              <Plus className="w-4 h-4" />Add provider
            </button>
          )}
        </div>
      </div>

      {query && (
        <p className="text-xs text-stone-500 mb-3" data-testid="providers-search-summary">
          Showing <b className="text-stone-700">{filteredList.length}</b> of {list.length} providers matching {`"`}<b className="text-stone-700">{query}</b>{`"`}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredList.length === 0 && (
          <div className="col-span-full text-center text-stone-500 py-12 bg-white border border-stone-200 rounded-xl" data-testid="providers-empty-state">
            {query ? <>No providers match {`"`}<b>{query}</b>{`"`}. Try a different search.</> : "No providers found."}
          </div>
        )}
        {filteredList.map((p) => (
          <div key={p.id} className="bg-white border border-stone-200 rounded-xl shadow-soft p-5 relative group" data-testid={`provider-card-${p.id}`}>
            {isSuper && (
              <button
                onClick={() => openEdit(p)}
                data-testid={`provider-edit-btn-${p.id}`}
                title="Edit provider"
                className="absolute top-3 right-3 p-1.5 rounded-lg bg-white border border-stone-200 text-stone-500 hover:text-brand-700 hover:border-brand-300 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center"><Stethoscope className="w-5 h-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-stone-900 truncate">{p.full_name}</div>
                <div className="text-xs text-stone-500 truncate flex items-center gap-1.5"><Mail className="w-3 h-3" />{p.email}</div>
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                p.role === "admin" || p.role === "organizer" ? "bg-warmth-100 text-warmth-500" : p.role === "doctor" ? "bg-brand-100 text-brand-700" : "bg-stone-100 text-stone-600"
              }`}>{p.role}</span>
            </div>
            <div className="mt-3 text-sm text-stone-600 space-y-1.5">
              {p.specialty && <div className="truncate flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-stone-400 shrink-0" />{p.specialty}</div>}
              {p.department && <div className="truncate flex items-center gap-1.5 text-stone-500"><Building2 className="w-3.5 h-3.5 text-stone-400 shrink-0" />{p.department}</div>}
              {p.phone && <div className="text-stone-500 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-stone-400 shrink-0" />{p.phone}</div>}
              {p.office_address && <div className="text-stone-500 flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 text-stone-400 shrink-0 mt-0.5" /><span className="truncate">{p.office_address}</span></div>}
            </div>
          </div>
        ))}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="add-provider-modal">
          <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md border border-stone-200">
            <div className="px-5 py-4 border-b border-stone-100 flex justify-between items-center">
              <h3 className="font-display font-semibold text-stone-900">{created ? "Provider created" : "Add provider"}</h3>
              <button onClick={() => { setShow(false); setCreated(null); }}><X className="w-5 h-5 text-stone-400" /></button>
            </div>
            {!created ? (
              <form onSubmit={submit} className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Full name*</label>
                    <input required data-testid="provider-name-input" value={form.full_name} onChange={(e)=>setForm({...form, full_name:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Email*</label>
                    <input required type="email" data-testid="provider-email-input" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Role*</label>
                    <select data-testid="provider-role-input" value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none">
                      <option value="doctor">Doctor</option><option value="nurse">Nurse</option><option value="admin">Admin</option><option value="organizer">Organizer</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Phone</label>
                    <input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Specialty</label>
                    <input data-testid="provider-specialty-input" value={form.specialty} onChange={(e)=>setForm({...form,specialty:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Department</label>
                    <input data-testid="provider-department-input" value={form.department} onChange={(e)=>setForm({...form,department:e.target.value})} placeholder="e.g. Medical Oncology" className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Office Address</label>
                  <input data-testid="provider-office-address-input" value={form.office_address} onChange={(e)=>setForm({...form,office_address:e.target.value})} placeholder="e.g. 200 Main St, Suite 5" className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                </div>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-5">
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">City</label>
                    <input data-testid="provider-city-input" value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})} placeholder="e.g. Boston" className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                  </div>
                  <div className="col-span-4">
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">State</label>
                    <select data-testid="provider-state-input" value={form.state} onChange={(e)=>setForm({...form,state:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none">
                      <option value="">--</option>
                      {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Zip</label>
                    <input data-testid="provider-zip-input" value={form.zip} onChange={(e)=>setForm({...form,zip:e.target.value})} placeholder="02118" maxLength={10} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Office Building / Follow-up Directions</label>
                  <textarea rows={2} data-testid="provider-office-directions-input" value={form.office_directions} onChange={(e)=>setForm({...form,office_directions:e.target.value})} placeholder="e.g. Building B, 3rd floor. Check in at reception desk before each visit." className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Password (optional — auto-generated if blank)</label>
                  <input type="text" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={()=>setShow(false)} className="px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50">Cancel</button>
                  <button data-testid="provider-submit-btn" type="submit" className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white text-sm font-semibold">Create</button>
                </div>
              </form>
            ) : (
              <div className="p-5 space-y-3">
                <div className="text-sm text-stone-700">Provider <b>{created.full_name}</b> has been created.</div>
                {created.welcome_email_sent && (
                  <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
                    A Welcome — Account Setup email with the login link was sent to <b>{created.email}</b>.
                  </div>
                )}
                {created._temp_password && (
                  <div className="px-3 py-2 rounded-lg bg-warmth-50 border border-warmth-100 text-sm">
                    <div className="font-medium text-stone-900">Temporary password (also emailed — share securely if needed)</div>
                    <div className="font-mono text-stone-800 mt-1">{created._temp_password}</div>
                  </div>
                )}
                <div className="flex justify-end">
                  <button onClick={()=>{ setShow(false); setCreated(null); }} className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white text-sm font-semibold">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="edit-provider-modal">
          <div className="bg-white rounded-2xl shadow-elevated w-full max-w-lg border border-stone-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-100 flex justify-between items-center">
              <h3 className="font-display font-semibold text-stone-900">Edit provider — {editing.full_name}</h3>
              <button onClick={() => setEditing(null)} data-testid="close-edit-provider"><X className="w-5 h-5 text-stone-400" /></button>
            </div>
            <form onSubmit={saveEdit} className="p-6 space-y-4">
              <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Full name</label>
                <input data-testid="edit-provider-name" required value={editForm.full_name} onChange={(e)=>setEditForm({...editForm,full_name:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Email</label>
                  <input data-testid="edit-provider-email" type="email" required value={editForm.email} onChange={(e)=>setEditForm({...editForm,email:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Phone</label>
                  <input data-testid="edit-provider-phone" value={editForm.phone} onChange={(e)=>setEditForm({...editForm,phone:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Specialty</label>
                  <input data-testid="edit-provider-specialty" value={editForm.specialty} onChange={(e)=>setEditForm({...editForm,specialty:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Status</label>
                  <select data-testid="edit-provider-status" value={editForm.status} onChange={(e)=>setEditForm({...editForm,status:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none">
                    <option value="active">Active</option><option value="inactive">Inactive</option>
                  </select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Role</label>
                  <select
                    data-testid="edit-provider-role"
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    disabled={editing?.id === user?.id}
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none disabled:bg-stone-50 disabled:text-stone-500"
                  >
                    <option value="doctor">Doctor</option>
                    <option value="nurse">Nurse</option>
                    <option value="admin">Admin</option>
                    <option value="organizer">Organizer</option>
                  </select>
                  {editing?.id === user?.id && (
                    <p className="text-[11px] text-stone-500 mt-1">You can&apos;t change your own role here.</p>
                  )}
                </div>
                <div /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Department</label>
                  <input data-testid="edit-provider-department" value={editForm.department} onChange={(e)=>setEditForm({...editForm,department:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Office Address</label>
                  <input data-testid="edit-provider-office-address" value={editForm.office_address} onChange={(e)=>setEditForm({...editForm,office_address:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
              </div>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-5"><label className="text-sm font-medium text-stone-700 mb-1.5 block">City</label>
                  <input data-testid="edit-provider-city" value={editForm.city} onChange={(e)=>setEditForm({...editForm,city:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
                <div className="col-span-4"><label className="text-sm font-medium text-stone-700 mb-1.5 block">State</label>
                  <select data-testid="edit-provider-state" value={editForm.state} onChange={(e)=>setEditForm({...editForm,state:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none">
                    <option value="">--</option>
                    {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
                  </select></div>
                <div className="col-span-3"><label className="text-sm font-medium text-stone-700 mb-1.5 block">Zip</label>
                  <input data-testid="edit-provider-zip" value={editForm.zip} onChange={(e)=>setEditForm({...editForm,zip:e.target.value})} maxLength={10} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
              </div>
              <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Office Building / Follow-up Directions</label>
                <textarea rows={2} data-testid="edit-provider-office-directions" value={editForm.office_directions} onChange={(e)=>setEditForm({...editForm,office_directions:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
              {editMsg.text && (
                <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border text-sm ${editMsg.kind === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                  {editMsg.kind === "success" ? <Check className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}<span>{editMsg.text}</span>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 font-medium hover:bg-stone-50">Cancel</button>
                <button data-testid="edit-provider-save" type="submit" disabled={editBusy} className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold disabled:opacity-60">{editBusy ? "Saving…" : "Save changes"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
